// Shopping cart. Items are deep `$state`, so `item.cartCount = n` is reactive and persisted;
// discount recalculation is debounced (300 ms) and aborts any in-flight request.
import { persisted } from './persisted.svelte.js';
import { profile } from './profile.svelte.js';
import { toast } from './toast.svelte.js';
import * as api from '$api/endpoints.js';
import { getStraightDiscountPrice, lineDiscountPrice, retailPrice } from './pricing.js';

/** @typedef {import('./pricing.js').CartProduct} CartProduct */

const DISCOUNT_DEBOUNCE_MS = 300;

// Checkout state that travels with the cart (all survive a reload; legacy keys are imported once).
/** @type {{ value: string, reset: () => void, key: string }} */
export const promoCode = persisted('promo_code', '', { legacyKey: 'promo_code' });
/** @type {{ value: Record<string, any>, reset: () => void, key: string }} */
export const deliveryAddress = persisted('delivery_address', {}, { legacyKey: 'delivery_address' });
/** 0 = own address, 1 = practice location, 2 = patient, 3 = other */
/** @type {{ value: number, reset: () => void, key: string }} */
export const alternateAddress = persisted('alternate_address', 0, {
	legacyKey: 'alternate_address'
});
/** @type {{ value: number | null, reset: () => void, key: string }} */
export const patientId = persisted('patient_id', null, { legacyKey: 'patient_id' });
/** @type {{ value: number | null, reset: () => void, key: string }} */
export const locationId = persisted('location_id', null, { legacyKey: 'location_id' });

class CartStore {
	/** @type {{ value: CartProduct[], reset: () => void, key: string }} */
	#items = persisted('cart', /** @type {CartProduct[]} */ ([]), { legacyKey: 'cart' });
	/** @type {{ value: Record<string, any>, reset: () => void, key: string }} */
	#discounts = persisted('multi_product_discounts', {}, { legacyKey: 'multi_product_discounts' });
	/** @type {{ value: boolean | null, reset: () => void, key: string }} */
	#scriptable = persisted('scriptable', /** @type {boolean | null} */ (null), {
		legacyKey: 'scriptable'
	});

	/** @type {ReturnType<typeof setTimeout> | undefined} */
	#timer;
	/** @type {AbortController | null} */
	#abort = null;

	get items() {
		return this.#items.value;
	}
	set items(value) {
		this.#items.value = value;
	}

	get multiProductDiscounts() {
		return this.#discounts.value;
	}

	/** False when an AU cart's retail value is over the scriptable limit. */
	get scriptable() {
		return this.#scriptable.value !== false;
	}

	get isEmpty() {
		return this.items.length === 0;
	}

	/** Total number of units in the cart. */
	get count() {
		return this.items.reduce((n, p) => n + (parseInt(String(p.cartCount)) || 0), 0);
	}

	/** Sum of line prices for purchasable lines (discontinued and display-only lines excluded). */
	get total() {
		let total = 0;
		for (const p of this.items) {
			if (p.product_type != 4 && !p.display_only) total += lineDiscountPrice(p);
		}
		return total;
	}

	/** @param {number | string} id */
	find(id) {
		return this.items.find((p) => p.id == id);
	}

	/**
	 * Add `count` units of `product` (negative to remove units). Returns the cart line, or
	 * undefined when the change was refused (a toast explains why).
	 * @param {CartProduct} product
	 * @param {number} [count]
	 * @param {boolean} [calcDiscounts]
	 */
	updateProduct(product, count = 1, calcDiscounts = true) {
		count = parseInt(String(count));
		if (typeof product === 'undefined' || isNaN(count)) return;

		if (product.display_only) {
			toast.warning('Display only product');
			return;
		}
		if (product.product_type == 4) {
			toast.warning('Product is discontinued');
			return;
		}

		const purchaseLimit = profile.purchaseLimit;
		const existing = this.find(product.id);
		const newQty = (existing?.cartCount || 0) + count;

		if (newQty <= 0) {
			if (existing) {
				this.items = this.items.filter((p) => p.id != product.id);
				toast.success('Product removed from Cart', 4000);
				this.scheduleRecalc();
			}
			this.#checkScriptable(purchaseLimit);
			return;
		}

		// Stock check (services and test kits are never stock limited).
		if (product.stock) {
			const stockQty = parseInt(String(product.stock.stock_qty)) || 0;
			if (
				product.product_type != 2 &&
				product.product_type != 1 &&
				stockQty < newQty &&
				!product.backorders_allowed
			) {
				toast.warning(
					'There is insufficient stock available. This item cannot be added to the cart'
				);
				return;
			}
		}

		// AU purchase limit on the whole cart.
		let total = 0;
		for (const line of this.items) {
			if (line.id != product.id)
				total += getStraightDiscountPrice(line) * Number(line.cartCount);
		}
		total += getStraightDiscountPrice(product) * newQty;
		if (purchaseLimit && total > purchaseLimit) {
			toast.warning(
				'Purchase Limit Exceeded Reduce your Cart to less than AUD$' +
					profile.auPurchaseLimit +
					' to Continue'
			);
			return;
		}

		if (existing) {
			if (existing.product_type != 1 && existing.product_type != 2 && existing.stock) {
				const stockQty = parseInt(String(existing.stock.stock_qty)) || 0;
				if (newQty > stockQty) {
					if (existing.backorders_allowed) {
						toast.warning(
							'There is insufficient stock available. This item will be backordered'
						);
					} else {
						toast.warning(
							'There is insufficient stock available. This item cannot be added to the cart'
						);
						return;
					}
				}
			}
			existing.cartCount = newQty;
		} else {
			this.items.push({ ...$state.snapshot(product), cartCount: count });
		}

		toast.success(count > 0 ? 'Product added to Cart' : 'Product removed from Cart', 4000);

		if (calcDiscounts) this.scheduleRecalc();
		this.#checkScriptable(purchaseLimit);

		return this.find(product.id);
	}

	/** @param {{ id: number | string }} product */
	removeProduct(product) {
		const before = this.items.length;
		this.items = this.items.filter((p) => p.id != product.id);
		if (this.items.length !== before) toast.success('Product removed from Cart', 4000);
		this.scheduleRecalc();
		this.#checkScriptable(profile.purchaseLimit);
	}

	clear() {
		this.#abort?.abort();
		clearTimeout(this.#timer);
		this.items = [];
		this.#discounts.value = {};
		this.#scriptable.value = true;
	}

	/** @param {string} code */
	setPromoCode(code) {
		promoCode.value = code;
		this.scheduleRecalc();
	}

	/** Debounced discount recalculation (cancels a pending one). */
	scheduleRecalc() {
		clearTimeout(this.#timer);
		if (this.items.length === 0) {
			this.#abort?.abort();
			this.#discounts.value = {};
			return;
		}
		this.#timer = setTimeout(() => this.recalc(), DISCOUNT_DEBOUNCE_MS);
	}

	/** Ask the backend for discounts and patch every cart line in place. */
	async recalc() {
		clearTimeout(this.#timer);
		if (this.items.length === 0) {
			this.#discounts.value = {};
			return;
		}

		this.#abort?.abort();
		const controller = new AbortController();
		this.#abort = controller;

		const cartData = this.items.map((p) => ({
			id: p.id,
			cartCount: p.cartCount || 0,
			supplier: $state.snapshot(p.supplier),
			product_type: p.product_type,
			your_price: $state.snapshot(p.your_price)
		}));

		try {
			const response = await api.calculateAllCartDiscounts(
				cartData,
				promoCode.value || '',
				controller.signal
			);
			if (!response || controller.signal.aborted) return;

			this.#discounts.value = response.multi_product_discounts || {};

			if (response.cart_items) {
				for (const line of this.items) {
					const item = response.cart_items[line.id];
					if (!item) continue;
					line.discount = item.discount;
					line.best_discount = item.best_discount;
					line.all_discounts = item.all_discounts;
					if (item.product) {
						line.stock = item.product.stock;
						line.your_price = item.product.your_price;
						line.excluded = item.product.excluded;
						line.retail_price = item.product.retail_price;
						line.backorders_allowed = item.product.backorders_allowed;
						line.test_kit_price = item.product.test_kit_price;
					}
				}
			}
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') return;
			console.error('Error calculating cart discounts:', error);
			this.#discounts.value = {};
		} finally {
			if (this.#abort === controller) this.#abort = null;
		}
	}

	/**
	 * AU practitioners may not script a cart whose retail value exceeds the purchase limit.
	 * @param {number} purchaseLimit
	 */
	#checkScriptable(purchaseLimit) {
		let totalRetail = 0;
		for (const p of this.items) {
			totalRetail += parseFloat(retailPrice(p)) * Number(p.cartCount);
		}
		this.#scriptable.value = !(purchaseLimit && totalRetail > purchaseLimit);
	}
}

export const cart = new CartStore();
